import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { IImpersonationContextComponentProps } from '../../../../models';
import { useAccountProfileQuery } from '../../../../queries';
import { canCustomizeContentWithAi } from '../../../../utils/hasContentPreferences';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { CampaignDateTimePicker } from '../../../components/campaigns/CampaignDateTimePicker';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { CalendarIcon } from '../../../components/svgs/icons/CalendarIcon';
import { CancelIconFill } from '../../../components/svgs/icons/CancelIconFill';
import { CheckmarkIconBold } from '../../../components/svgs/icons/CheckmarkIconBold';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../components/svgs/icons/TrashIcon';
import { Toggle } from '../../../components/Toggle';
import { aceGray, brandPrimary, destructive, success, urgentDate } from '../../../styles/colors';
import { bs } from '../../../styles/styles';

export const styleSheet = StyleSheet.create({
	successMessage: {
		color: THEME.colors.success.dark,
		fontSize: 14,
		margin: 0,
		lineHeight: 1.5,
	},
	actions: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 12,
	},
	addDateButton: {
		color: brandPrimary,
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		flex: '1 1 0%',
	},
	recommendedDateContainer: {
		display: 'flex',
		alignItems: 'center',
		fontSize: 12,
		gap: 8,
		flexShrink: 0,
	},
	templateTitle: {
		fontWeight: 600,
		fontSize: 18,
		overflow: 'hidden',
		'-webkit-box-orient': 'vertical',
		'-webkit-line-clamp': '2',
		// @ts-ignore
		// display: '-webkit-box',
		display: 'flex',
		alignItems: 'center',

		gap: 8,
	},
	headerRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		gap: THEME.spacing.md,
	},
	MultiCampaignTemplateItem: {
		display: 'flex',
		alignItems: 'center',
		gap: 20,
	},
	errorMessage: {
		color: urgentDate,
		fontSize: 14,
		margin: 0,
	},
	generateContentInfoContainer: {
		display: 'flex',
		width: '100%',
	},
	showOriginalContentToggle: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		flexGrow: 1,
		gap: 8,
	},
	showOriginalContentLabel: {
		fontSize: 12,
	},
});

export function MultiCampaignTemplateItem({
	name,
	scheduleDate,
	expirationDate,
	onUpdateDate,
	onRemove,
	children,
	showSuccess,
	sendFrom,
	sendFromUserId,
	error,
	postedOn,
	isEmail = false,
	onEdit,
	impersonationContext,
	generationFailed,
	onRefineContent,
	isGeneratingContent,
	isGenerateContentDisabled,
	applyBrandingToPost,
	undoBrandingToPost,
	onShowOriginalContent,
	showOriginalContent,
	schedulingState,
	isEditing,
}: React.PropsWithChildren<
	{
		name: React.ReactNode;
		onRemove: () => void;
		onEdit?: () => void;
		onUpdateDate: (newDate: Date) => void;
		scheduleDate?: Date;
		expirationDate?: Date;
		showSuccess?: boolean;
		sendFrom: Api.ISendEmailFrom;
		sendFromUserId?: string;
		error?: Api.IOperationResultNoValue;
		postedOn?: Date | string;
		isEmail?: boolean;
		generationFailed?: boolean;
		onRefineContent?: () => void;
		isGeneratingContent?: boolean;
		isGenerateContentDisabled?: boolean;
		applyBrandingToPost?: { onClick: () => void; isLoading: boolean; disabled: boolean };
		undoBrandingToPost?: { onClick: () => void; disabled: boolean };
		onShowOriginalContent?: () => void;
		showOriginalContent?: boolean;
		schedulingState?: 'scheduled' | 'failed';
		isEditing?: boolean;
	} & IImpersonationContextComponentProps
>) {
	const [isShowingCalendar, setIsShowingCalendar] = React.useState(false);
	const [isRemoving, setIsRemoving] = React.useState(false);
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });

	const onDateSave = (newValue: Date) => {
		onUpdateDate(newValue);
		setIsShowingCalendar(false);
	};

	const onDateCancel = () => {
		setIsShowingCalendar(false);
	};

	const onDeleteConfirmationFinished = (shouldDelete: boolean) => {
		if (shouldDelete) {
			onRemove();
		}
		setIsRemoving(false);
	};

	const showRemoveConfirmation = () => {
		setIsRemoving(true);
	};

	const toggleId = React.useId();

	return (
		<div className={css(styleSheet.MultiCampaignTemplateItem)}>
			<div className={css(styleSheet.contentContainer)}>
				<header className={css(styleSheet.headerRow)}>
					<div>
						<div className={css(styleSheet.templateTitle)}>
							{name}{' '}
							{schedulingState === 'scheduled' ? (
								<CheckmarkIconBold fillColor={success} />
							) : schedulingState === 'failed' ? (
								<CancelIconFill />
							) : null}
						</div>
						{schedulingState === 'scheduled' ? (
							<p className={css(styleSheet.successMessage)}>Successfully scheduled</p>
						) : null}
						{error ? <p className={css(styleSheet.errorMessage)}>Failed to post: {error.systemMessage}</p> : null}
						{postedOn ? (
							<p className={css(styleSheet.errorMessage)}>
								{isEmail ? 'Email sent' : 'Posted'} from this account on {moment(postedOn).format('MM/DD/YYYY')}
							</p>
						) : null}
					</div>
					{isEditing ? null : (
						<div className={css(styleSheet.recommendedDateContainer)}>
							<Popover open={isShowingCalendar} onOpenChange={setIsShowingCalendar}>
								<PopoverTrigger asChild>
									<Button variant='secondary' size='sm' disabled={showSuccess}>
										<CalendarIcon />{' '}
										{!scheduleDate ? 'Add send date' : moment(scheduleDate).format('ddd, MMM Do h:mm a')}
										{showSuccess ? <CheckmarkIconBold fillColor={success} /> : null}
									</Button>
								</PopoverTrigger>
								<PopoverContent styles={[bs.p0]} side='left'>
									<PopoverArrow />
									<CampaignDateTimePicker
										initialValue={scheduleDate || moment().toDate()}
										onSave={onDateSave}
										onCancel={onDateCancel}
										expirationDate={expirationDate}
										sendFrom={sendFrom}
										sendFromUserId={sendFromUserId}
										ignoreDisabledDays={!isEmail}
										ignoreEmailTimeIntervals={!isEmail}
										{...(isEmail ? { impersonationContext } : {})}
									/>
								</PopoverContent>
							</Popover>
							{onEdit ? (
								<Button variant='secondary' size='sm' disabled={showSuccess} onClick={onEdit}>
									<EditItemPenIcon fillColor={brandPrimary} /> Edit
								</Button>
							) : null}
							{onRefineContent &&
							canCustomizeContentWithAi(accountProfileQuery.data, !!impersonationContext?.isValid) ? (
								<AiButton
									size='sm'
									title='Edit with AI'
									onClick={() => onRefineContent()}
									isLoading={isGeneratingContent}
									disabled={showSuccess || isGenerateContentDisabled}
								/>
							) : null}
							{applyBrandingToPost ? (
								<Button
									variant='secondary'
									size='sm'
									onClick={applyBrandingToPost.onClick}
									isLoading={applyBrandingToPost.isLoading}
									disabled={showSuccess || applyBrandingToPost.disabled}
								>
									Apply branding
								</Button>
							) : null}
							{undoBrandingToPost ? (
								<Button
									variant='secondary'
									size='sm'
									onClick={undoBrandingToPost.onClick}
									disabled={showSuccess || undoBrandingToPost.disabled}
								>
									Undo branding
								</Button>
							) : null}
							<Button
								variant='destructive-secondary'
								size='sm'
								disabled={showSuccess || !onRemove}
								onClick={showRemoveConfirmation}
							>
								<TrashIcon fillColor={destructive} />
							</Button>
						</div>
					)}
				</header>
				<div className={css(styleSheet.generateContentInfoContainer)}>
					{generationFailed ? <p className={css(styleSheet.errorMessage)}>Failed to generate content</p> : null}

					{onShowOriginalContent && (
						<div className={css(styleSheet.showOriginalContentToggle)}>
							<Toggle
								text='Show original content'
								id={toggleId}
								isOn={showOriginalContent}
								onToggleCheckChanged={onShowOriginalContent}
								uncheckedColor={aceGray}
							/>
						</div>
					)}
				</div>
				{children}
			</div>
			{isRemoving ? (
				<DeleteConfirmation
					bodyText={`Are you sure you'd like to remove ${name} from selected suggestions?`}
					destructiveButtonText='Remove'
					isOpen={isRemoving}
					onFinish={onDeleteConfirmationFinished}
				/>
			) : null}
		</div>
	);
}
